import { Text, View } from "react-native";
import {
  ActionBanner,
  ClinicalDataStatus,
  MoonToggle,
  Row,
  EmptyState,
  DialogIcon,
  semanticColors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import {
  GroupedCard,
  YouIllustration,
  YOU_ILLUSTRATION_SIZE,
} from "../components";
import { ICONS, ROW_ICONS } from "../icons";
import type { Study } from "../types";

type Props = {
  studies: Study[];
  anonymousDataContribution: boolean;
  anonymousDataContributionLoadFailed: boolean;
  anonymousDataContributionSaveFailed: boolean;
  onRetryAnonymousDataContributionLoad: () => void;
  isRetryingAnonymousDataContributionLoad: boolean;
  onAnonymousDataContributionChange: (next: boolean) => void;
};

function StudySummary({ study, divider }: { study: Study; divider: boolean }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: semanticColors.border.strong,
      }}
    >
      <YouIllustration source={ICONS.beaker} style={{ marginTop: 1 }} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 20,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
        >
          {study.title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textLight,
            marginTop: 2,
          }}
        >
          {study.compensation
            ? `${study.institution} · ${study.compensation}`
            : study.institution}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textFaint,
            marginTop: 5,
          }}
        >
          {study.matchReason}
        </Text>
      </View>
    </View>
  );
}

function EmptyStudies() {
  const t = useTranslations();

  return (
    <EmptyState
      testID="research-studies-empty-state"
      density="compact"
      titleVariant="featured"
      illustration={
        <DialogIcon source={ROW_ICONS.research} width={68} height={80} />
      }
      title={t("you.research_studies.no_eligible_studies_at_the_moment")}
      subtitle={t("you.research_studies.relevant_studies_will_appear_here")}
      style={{ paddingVertical: 28 }}
    />
  );
}

/**
 * The dialog title supplies the section heading. This content intentionally
 * contains only the relevant study state and the persisted research choice.
 */
export default function ResearchStudies({
  studies,
  anonymousDataContribution,
  anonymousDataContributionLoadFailed,
  anonymousDataContributionSaveFailed,
  onRetryAnonymousDataContributionLoad,
  isRetryingAnonymousDataContributionLoad,
  onAnonymousDataContributionChange,
}: Props) {
  const t = useTranslations();

  return (
    <View>
      <GroupedCard>
        {studies.length > 0 ? (
          studies.map((study, index) => (
            <StudySummary key={study.id} study={study} divider={index > 0} />
          ))
        ) : (
          <EmptyStudies />
        )}

        <Row
          surface="plain"
          leading={<YouIllustration source={ICONS.anonymousDataContribution} />}
          leadingSize={YOU_ILLUSTRATION_SIZE}
          title={t("you.research_studies.anonymous_label")}
          subtitle={t("you.research_studies.anonymous_description")}
          trailing={
            <MoonToggle
              value={anonymousDataContribution}
              onChange={onAnonymousDataContributionChange}
              testID="anonymous-data-contribution-toggle"
              accessibilityLabel={t("you.research_studies.anonymous_label")}
              accessibilityHint={t(
                "you.research_studies.anonymous_description",
              )}
            />
          }
          gap={12}
          paddingHorizontal={16}
          paddingVertical={14}
          borderRadius={0}
          titleStyle={{
            fontSize: 15,
            lineHeight: 20,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
          subtitleStyle={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textFaint,
          }}
          style={{
            borderRadius: 0,
            borderTopWidth: 1,
            borderTopColor: semanticColors.border.strong,
          }}
        />
      </GroupedCard>

      {anonymousDataContributionLoadFailed ? (
        <ClinicalDataStatus
          state="stale"
          message={t("you.research_studies.contribution_load_failed")}
          retryLabel={t("clinical_data.retry")}
          retryingLabel={t("clinical_data.retrying")}
          onRetry={onRetryAnonymousDataContributionLoad}
          isRetrying={isRetryingAnonymousDataContributionLoad}
          testID="research-contribution-stale"
        />
      ) : anonymousDataContributionSaveFailed ? (
        <ActionBanner
          variant="important"
          message={t("you.research_studies.contribution_save_failed")}
        />
      ) : null}
    </View>
  );
}
